import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Premium Mallorca cove hero - high resolution editorial image
const heroImage = "https://images.unsplash.com/photo-1537042145424-98c3022ed567?auto=format&fit=crop&w=3200&q=90";

// Hero Section with uploaded image
const HeroSection = () => {
  const { t } = useLanguage();

  return (
    <section data-testid="hero-section" className="relative min-h-screen flex items-end">
      {/* Background Image with microcontrast enhancement */}
      <div className="absolute inset-0">
        <img
          src={heroImage}
          alt="Mallorca coastline view"
          className="w-full h-full object-cover hero-image-enhanced"
        />
        {/* Premium overlay - dark navy with gradient */}
        <div className="hero-overlay absolute inset-0" />
        {/* Film grain layer */}
        <div className="hero-grain absolute inset-0 pointer-events-none" />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 pb-16 md:pb-24">
        <div className="max-w-3xl">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-white/70 text-xs font-bold tracking-[0.2em] uppercase mb-4"
          >
            {t.hero.location}
          </motion.p>
          
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="font-heading text-4xl md:text-6xl lg:text-7xl text-white font-medium tracking-tight leading-[1.1] mb-6"
          >
            {t.hero.headline}
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-white/80 text-lg md:text-xl leading-relaxed mb-10 max-w-xl"
          >
            {t.hero.subheadline}
          </motion.p>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="flex flex-col sm:flex-row gap-4"
          >
            <Link
              to="/contact"
              data-testid="hero-cta-primary"
              className="btn-primary inline-flex items-center justify-center gap-2"
            >
              {t.hero.ctaPrimary}
              <ArrowRight size={16} />
            </Link>
            <Link
              to="/services"
              data-testid="hero-cta-secondary"
              className="btn-hero-secondary inline-flex items-center justify-center"
            >
              {t.hero.ctaSecondary}
            </Link>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden md:block"
      >
        <div className="w-[1px] h-16 bg-white/30 relative">
          <motion.div
            animate={{ y: [0, 40, 0] }}
            transition={{ repeat: Infinity, duration: 2 }}
            className="w-1 h-4 bg-white absolute -left-[1px] top-0"
          />
        </div>
      </motion.div>
    </section>
  );
};

// Services Overview Section - 3 Main Pillars Only
const ServicesOverview = () => {
  const { t, language } = useLanguage();

  // Only show 3 main service pillars on homepage
  const mainServices = [
    { 
      key: 'propertyManagement', 
      slug: 'property-management',
      image: 'https://images.unsplash.com/photo-1613977257363-707ba9348227?auto=format&fit=crop&w=1200&q=80'
    },
    { 
      key: 'maintenanceCoordination', 
      slug: 'maintenance',
      image: 'https://images.pexels.com/photos/27398805/pexels-photo-27398805.jpeg?auto=compress&cs=tinysrgb&w=1200'
    },
    { 
      key: 'renovations', 
      slug: 'renovations',
      image: 'https://customer-assets.emergentagent.com/job_cd17f3e5-2b43-4862-92f7-9dbae9dcf0a1/artifacts/zvcgcofy_dominik-8AZ_2om65TU-unsplash.jpg'
    },
  ];

  const descriptions = {
    EN: {
      propertyManagement: 'Overall management and coordination of your Mallorca property while you are away.',
      maintenanceCoordination: 'Coordination of technical repairs and maintenance through trusted local contractors.',
      renovations: 'Expert coordination of renovation projects and technical upgrades.',
    },
    ES: {
      propertyManagement: 'Gestión integral y coordinación de su propiedad en Mallorca mientras está fuera.',
      maintenanceCoordination: 'Coordinación de reparaciones técnicas y mantenimiento a través de contratistas locales de confianza.',
      renovations: 'Coordinación experta de proyectos de reforma y mejoras técnicas.',
    }
  };

  return (
    <section data-testid="services-overview" className="py-28 md:py-36 bg-cream">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-2xl mb-20">
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-stone-500 mb-4">
            {t.servicesOverview.label}
          </p>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-navy tracking-tight mb-8">
            {t.servicesOverview.title}
          </h2>
          <p className="text-stone-600 leading-relaxed text-lg">
            {t.servicesOverview.description}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
          {mainServices.map((service, index) => {
            const title = t.servicesTitles[service.key];

            return (
              <motion.div
                key={service.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Link
                  to={`/services#${service.slug}`}
                  data-testid={`service-tile-${service.slug}`}
                  className="service-card-premium group block bg-white border border-stone-100 overflow-hidden"
                >
                  {/* Service Image */}
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={service.image}
                      alt={title}
                      className={`service-card-image w-full h-full object-cover ${
                        service.key === 'propertyManagement' ? 'object-[center_60%]' : ''
                      }${
                        service.key === 'maintenanceCoordination' ? 'object-[center_35%]' : ''
                      }`}
                    />
                  </div>
                  {/* Service Content */}
                  <div className="p-8 md:p-10">
                    <h3 className="font-heading text-2xl md:text-[1.75rem] text-navy leading-tight mb-4 tracking-tight">
                      {title}
                    </h3>
                    <p className="text-stone-500 text-base leading-relaxed mb-6">
                      {descriptions[language][service.key]}
                    </p>
                    <span className="learn-more-link inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-navy">
                      {language === 'ES' ? 'Ver Más' : 'Learn More'} <ChevronRight size={14} />
                    </span>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-16 text-center">
          <Link
            to="/services"
            data-testid="view-all-services-btn"
            className="btn-secondary inline-flex items-center gap-2"
          >
            {t.servicesOverview.viewAll}
            <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
};

// Why Us Section
const WhyUsSection = () => {
  const { t } = useLanguage();

  return (
    <section data-testid="why-us-section" className="py-28 md:py-36 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-stone-500 mb-4">
              {t.whyUs.label}
            </p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-navy tracking-tight mb-8">
              {t.whyUs.title}
            </h2>
            <p className="text-stone-600 leading-relaxed mb-12 text-lg">
              {t.whyUs.description}
            </p>
            <div className="space-y-8">
              {t.whyUs.points.map((point, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex gap-4"
                >
                  <div className="w-1 bg-navy flex-shrink-0" />
                  <div>
                    <h3 className="font-medium text-navy mb-2">{point.title}</h3>
                    <p className="text-stone-600">{point.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
          <div className="relative overflow-hidden">
            <img
              src="https://customer-assets.emergentagent.com/job_cd17f3e5-2b43-4862-92f7-9dbae9dcf0a1/artifacts/a0y2oye4_IMG_4011.jpg"
              alt="Mallorcan villa with infinity pool and Mediterranean sea view"
              className="w-full h-auto object-cover"
            />
            {/* Subtle dark overlay for contrast */}
            <div className="absolute inset-0 bg-navy/10" />
          </div>
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const { t, language } = useLanguage();
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await axios.get(`${API}/testimonials`);
        setTestimonials(response.data);
      } catch (error) {
        console.error('Error fetching testimonials:', error);
        setTestimonials([
          {
            id: "1",
            name: "Thomas & Elisabeth M.",
            location: "Son Vida, Mallorca",
            quote: language === 'ES' 
              ? "Tranquilidad total. Los informes estructurados nos mantienen informados, y los problemas se resuelven antes de convertirse en problemas."
              : "Complete peace of mind. The structured reporting keeps us informed, and issues are resolved before they become problems.",
            highlight: language === 'ES' ? "Fiabilidad" : "Reliability"
          },
          {
            id: "2",
            name: "Gilbert & Jane C.",
            location: "Bendinat, Mallorca",
            quote: language === 'ES'
              ? "Mario coordinó la pintura, limpieza y preparación de nuestro apartamento antes de la sesión fotográfica inmobiliaria. Todo estuvo perfectamente organizado y la propiedad lucía impecable."
              : "Mario coordinated painting, cleaning and preparation of our apartment before the real estate photo session. Everything was perfectly organized and the property looked immaculate.",
            highlight: language === 'ES' ? "Coordinación" : "Coordination"
          },
          {
            id: "3",
            name: "Henrik J.",
            location: "Port d'Andratx, Mallorca",
            quote: language === 'ES'
              ? "Atención al detalle excepcional. Desde coordinar contratistas hasta supervisar nuestra piscina y jardín, todo funciona perfectamente."
              : "Exceptional attention to detail. From coordinating contractors to supervising our pool and garden, everything runs seamlessly.",
            highlight: language === 'ES' ? "Atención al Detalle" : "Attention to Detail"
          },
          {
            id: "4",
            name: "Karina",
            location: "Puerto Adriano, Mallorca",
            quote: language === 'ES'
              ? "Mientras estaba en el extranjero, Mario gestionó la renovación de la cocina y la pintura de mi apartamento. Todo se manejó profesionalmente y el resultado superó mis expectativas."
              : "While I was abroad, Mario managed the kitchen renovation and repainting of my apartment. Everything was handled professionally and the result exceeded my expectations.",
            highlight: language === 'ES' ? "Gestión de Reformas" : "Renovation Management"
          },
          {
            id: "5",
            name: "Sarah & Michael W.",
            location: "Calvià, Mallorca",
            quote: language === 'ES'
              ? "Finalmente, un equipo que entiende la gestión proactiva de propiedades. Nuestra casa siempre está lista para nuestra llegada, cada vez."
              : "Finally, a team that understands proactive property management. Our home is always ready for our arrival, every time.",
            highlight: language === 'ES' ? "Profesionalismo" : "Professionalism"
          },
          {
            id: "6",
            name: "Viviane G.",
            location: "San Agustín, Mallorca",
            quote: language === 'ES'
              ? "Mario restauró la barandilla frente al mar de nuestra propiedad y el resultado es precioso. Es reconfortante tener a alguien de confianza supervisando las cosas cuando no estamos en Mallorca."
              : "Mario restored the sea-front railing of our property and the result is beautiful. It's reassuring to have someone reliable overseeing things when we are not in Mallorca.",
            highlight: language === 'ES' ? "Confianza" : "Trust"
          }
        ]);
      }
    };
    fetchTestimonials();
  }, [language]);

  return (
    <section data-testid="testimonials-section" className="py-28 md:py-36 bg-stone-50">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-2xl mb-20">
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-stone-500 mb-4">
            {t.testimonials.label}
          </p>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-navy tracking-tight">
            {t.testimonials.title}
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              data-testid={`testimonial-card-${index}`}
              className="testimonial-card relative"
            >
              <span className="quote-mark">"</span>
              <div className="relative z-10">
                <span className="inline-block text-xs font-bold tracking-widest uppercase text-navy/50 mb-4">
                  {testimonial.highlight}
                </span>
                <p className="text-stone-600 leading-relaxed mb-6 italic">
                  "{testimonial.quote}"
                </p>
                <div>
                  <p className="font-medium text-navy">{testimonial.name}</p>
                  <p className="text-sm text-stone-500">{testimonial.location}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};


// CTA Section
const CTASection = () => {
  const { t } = useLanguage();

  return (
    <section data-testid="cta-section" className="py-16 md:py-20 bg-navy">
      <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-2xl mx-auto"
        >
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-white tracking-tight mb-8">
            {t.cta.title}
          </h2>
          <p className="text-white/70 leading-relaxed mb-10">
            {t.cta.description}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              data-testid="cta-primary-btn"
              className="bg-white text-navy px-8 py-4 text-xs font-medium uppercase tracking-widest hover:bg-stone-100 transition-all inline-flex items-center justify-center gap-2"
            >
              {t.cta.buttonPrimary}
              <ArrowRight size={16} />
            </Link>
            <Link
              to="/approach"
              data-testid="cta-secondary-btn"
              className="border border-white/30 text-white px-8 py-4 text-xs font-medium uppercase tracking-widest hover:bg-white/10 transition-all inline-flex items-center justify-center"
            >
              {t.cta.buttonSecondary}
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Main HomePage Component
const HomePage = () => {
  return (
    <div data-testid="home-page">
      <Helmet>
        <title>Mallorca Property Management | Balearic Home Solutions</title>
        <meta name="description" content="Private property management in Mallorca. Local oversight, transparent reporting and complete peace of mind for international homeowners. Based in Calvià." />
        <link rel="canonical" href="https://balearichomesolutions.com/" />
      </Helmet>
      <HeroSection />
      <ServicesOverview />
      <WhyUsSection />
      <TestimonialsSection />
      <CTASection />
    </div>
  );
};

export default HomePage;
